import React, { useState, useEffect } from "react";
import { X, User, Phone, Mail, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import apiClient from "@/utils/apiClient";
import { toast } from "sonner";

const ParentAddModal = ({ onClose }) => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    father_name: "",
    mother_name: "",
    phone: "",
    secondary_phone: "",
    email: "",
    address: "",
    local_guardian_name: "",
    local_guardian_phone: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

/*************  ✨ Windsurf Command 🌟  *************/
  /**
   * Handles the submission of the parent add form
   * @param {Event} e - The submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check that required fields have been filled in
    // Check required fields
    if (!formData.phone?.trim()) {
      // If phone number is empty, show error message
      toast.error("Phone number is required");
      return;
    }

    if (!formData.email?.trim()) {
      // If email address is empty, show error message
      toast.error("Email address is required");
      return;
    }

    if (!formData.address?.trim()) {
      // If address is empty, show error message
      toast.error("Address is required");
      return;
    }

    if (!formData.father_name?.trim()) {
      // If father's name is empty, show error message
      toast.error("Father's name is required");
      return;
    }

    // Validate email address format
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      // If email address is not in the correct format, show error message
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate phone number format - ensure it's exactly 10 digits
    // Phone number validation - ensure it's exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      // If phone number is not in the correct format, show error message
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    // Validate optional fields
    // Optional field validations
    if (
      formData.secondary_phone &&
      !phoneRegex.test(formData.secondary_phone)
    ) {
      // If secondary phone number is provided but not in the correct format, show error message
      toast.error(
        "Secondary phone number must be exactly 10 digits if provided"
      );
      return;
    }

    if (
      formData.local_guardian_phone &&
      !phoneRegex.test(formData.local_guardian_phone)
    ) {
      // If local guardian phone number is provided but not in the correct format, show error message
      toast.error(
        "Local guardian phone number must be exactly 10 digits if provided"
      );
      return;
    }

    // Show loading indicator
    setLoading(true);

    try {
      // Backend now handles updating both parent and all children in one call
      await apiClient.post(`/v1/admin/parents`, {
        name: `${formData.father_name} & ${formData.mother_name}`,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        father_name: formData.father_name,
        mother_name: formData.mother_name,
        secondary_phone: formData.secondary_phone,
        local_guardian_name: formData.local_guardian_name,
        local_guardian_phone: formData.local_guardian_phone,
      });

      // Show success message and close modal on success
      toast.success("Parent created successfully");
      onClose();
      fetchParents();
    } catch (error) {
      // Show error message on failure
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Failed to create parent");
    } finally {
      // Hide loading indicator on completion
      setLoading(false);
    }
  };
 
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
              Add Parent Information
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Parent Details (Editable)
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father Name *
                  </label>
                  <input
                    type="text"
                    value={formData.father_name}
                    onChange={(e) => handleChange("father_name", e.target.value)}
                    placeholder="e.g., John Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother Name
                  </label>
                  <input
                    type="text"
                    value={formData.mother_name}
                    onChange={(e) => handleChange("mother_name", e.target.value)}
                    placeholder="e.g., Jane Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Primary Phone *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="Enter 10-digit mobile number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Secondary Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.secondary_phone}
                    onChange={(e) => handleChange("secondary_phone", e.target.value)}
                    placeholder="Alternate contact number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="parent@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    rows={2}
                    placeholder="House/Flat No., Street, City, State - PIN Code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Guardian Name
                  </label>
                  <input
                    type="text"
                    value={formData.local_guardian_name}
                    onChange={(e) => handleChange("local_guardian_name", e.target.value)}
                    placeholder="e.g., Robert Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Guardian Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.local_guardian_phone}
                    onChange={(e) => handleChange("local_guardian_phone", e.target.value)}
                    placeholder="Enter 10-digit mobile number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={onClose}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Adding..." : "Add Parent"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentAddModal;
